import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { getUser } from '../Api/getUser';
import * as authActions from '../Components/Auth/authActions';
import { PATH } from '../Constants/paths';
import { isClientServiceSuspended } from '../utils/clientServiceStatus';
import {
  isBillingReactivatedReturn,
  REACTIVATION_MAX_ATTEMPTS,
  REACTIVATION_POLL_MS,
  STRIPE_REACTIVATION_RETURN_PATH,
  subscriptionInactivePath,
} from '../utils/billingReactivation';
import { saveRedirectUrl } from '../utils/redirectAfterLogin';

export type BillingReactivationPollState = {
  isReactivationReturn: boolean;
  isPolling: boolean;
  timedOut: boolean;
};

/** Poll `/users/me` after Stripe returns with `?billing=reactivated`. */
export function useBillingReactivationPoll(): BillingReactivationPollState {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useSelector((state: AppState) => state.auth.isAuthenticated);
  const [isPolling, setIsPolling] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const pollingRef = useRef(false);

  const isReactivationReturn = isBillingReactivatedReturn(location.search);

  useEffect(() => {
    if (!isReactivationReturn) {
      pollingRef.current = false;
      setIsPolling(false);
      setTimedOut(false);
      return;
    }

    if (pollingRef.current) {
      return;
    }
    pollingRef.current = true;
    setIsPolling(true);
    setTimedOut(false);

    if (!isAuthenticated) {
      saveRedirectUrl(STRIPE_REACTIVATION_RETURN_PATH);
      navigate(PATH.LOGIN, { replace: true });
      pollingRef.current = false;
      setIsPolling(false);
      return;
    }

    let attempts = 0;
    let cancelled = false;
    let intervalId: number | undefined;

    const finishPolling = (stillSuspended: boolean) => {
      pollingRef.current = false;
      setIsPolling(false);
      if (stillSuspended) {
        setTimedOut(true);
        navigate(subscriptionInactivePath(), { replace: true });
      }
    };

    const pollOnce = () => {
      getUser(
        (freshUser) => {
          if (cancelled) {
            return;
          }
          dispatch(authActions.userLoaded(freshUser));
          if (!isClientServiceSuspended(freshUser)) {
            cancelled = true;
            if (intervalId !== undefined) {
              window.clearInterval(intervalId);
            }
            pollingRef.current = false;
            setIsPolling(false);
            navigate(PATH.Sales_Home, { replace: true });
          } else if (attempts >= REACTIVATION_MAX_ATTEMPTS) {
            cancelled = true;
            if (intervalId !== undefined) {
              window.clearInterval(intervalId);
            }
            finishPolling(true);
          }
        },
        () => {
          if (cancelled) {
            return;
          }
          if (attempts >= REACTIVATION_MAX_ATTEMPTS) {
            cancelled = true;
            if (intervalId !== undefined) {
              window.clearInterval(intervalId);
            }
            finishPolling(true);
          }
        }
      );
    };

    pollOnce();
    intervalId = window.setInterval(() => {
      if (cancelled) {
        return;
      }
      attempts += 1;
      pollOnce();
    }, REACTIVATION_POLL_MS);

    return () => {
      cancelled = true;
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
      pollingRef.current = false;
      setIsPolling(false);
    };
  }, [dispatch, isAuthenticated, isReactivationReturn, location.search, navigate]);

  return { isReactivationReturn, isPolling, timedOut };
}
